/**
 * The EventSource lifecycle — SPEC_5 §4.3, on SPEC_2's stream contract.
 *
 * **Native `EventSource`.** *Rejected: a `fetch`-based reader*, which could set `Last-Event-ID` as a
 * header — but `stream.py` accepts `?last=` precisely because a browser's EventSource cannot, so the
 * capability is already covered, and native gives reconnect-with-`Last-Event-ID` for free, which is
 * the one thing a laptop that sleeps needs.
 *
 * **One stream per tab**, opened once at the app root, never per panel.
 *
 * **History first, then the tail.** The payloads load over plain GET; the tab then opens
 * `/api/stream?last=<the LOWEST X-Stream-Head it holds>`. The lowest, not the newest: a read that
 * answered earlier would otherwise miss everything between its answer and the newest one.
 *
 * **Two failures that must not look alike.** No `heartbeat` for 30 s with reconnects failing is
 * *server unreachable*, and says nothing about the worker, which may still be running. A
 * `heartbeat` arriving with `alive: false` is the worker itself, and the takeover banner says so.
 * The API's own 5-second ticker exists so the two are distinguishable, and the watchdog below only
 * ever sets the STREAM's status.
 */

import { api } from "../api/client";
import { currentSession } from "../api/auth";
import { useSession } from "../store/session";
import { useCompetition } from "../store/competition";
import { NAMES, apply } from "./apply";

/** No heartbeat for this long, with the connection not live, is *server unreachable*. The API
 *  emits one every 5 s whether or not the worker is writing, so 30 s is six missed ticks. */
const SILENCE_MS = 30_000;

let source: EventSource | null = null;
let watchdog: number | null = null;

async function onClosed(): Promise<void> {
  // EventSource closes without reconnecting on an HTTP error, which is how a 401 reaches us. The
  // stream re-checks the session every five seconds and closes on failure, so this is the expected
  // path for a session that ended. `GET /api/auth` says which of the two it was.
  const answer = await currentSession().catch(() => null);
  if (answer && !answer.signedIn) {
    useSession.getState().bounced(answer.state, answer.says);
    close();
    return;
  }
  // Signed in (or unanswered) and yet the stream closed: treat it as a drop and try again
  // ourselves, since the browser will not.
  window.setTimeout(() => open(), 2_000);
}

/** Opened once, at the app root. Calling it again while a stream is live is a no-op. */
export function open(): void {
  if (source && source.readyState !== EventSource.CLOSED) return;

  const store = useCompetition.getState();
  const last = store.lowestHead();
  const url = api(`/stream${last ? `?last=${encodeURIComponent(last)}` : ""}`);
  const reconnecting = store.stream.lastEventId !== null;
  store.setStream({ status: reconnecting ? "reconnecting" : "connecting" });

  const it = new EventSource(url, { withCredentials: true });
  source = it;

  it.onopen = () => {
    useCompetition.getState().setStream({ status: "live", droppedAt: null });
  };

  it.onerror = () => {
    if (it.readyState === EventSource.CLOSED) {
      void onClosed();
      return;
    }
    // CONNECTING: the browser is retrying on its own, with `Last-Event-ID`. The page keeps its
    // last values and the header says it is reconnecting.
    const now = useCompetition.getState();
    now.setStream({
      status: "reconnecting",
      droppedAt: now.stream.droppedAt ?? now.heartbeat?.now ?? null,
    });
  };

  for (const name of NAMES) {
    it.addEventListener(name, (raw) => {
      const event = raw as MessageEvent<string>;
      let data: Record<string, unknown> = {};
      try {
        data = event.data ? (JSON.parse(event.data) as Record<string, unknown>) : {};
      } catch {
        return; // a frame we cannot read is a frame we do not act on
      }
      if (event.lastEventId) {
        useCompetition.getState().setStream({ status: "live", lastEventId: event.lastEventId });
      }
      apply(name, data);
    });
  }

  if (watchdog === null) {
    watchdog = window.setInterval(() => {
      const state = useCompetition.getState();
      const seen = state.stream.lastHeartbeatAt;
      const silent = seen === null || Date.now() - seen > SILENCE_MS;
      if (silent && state.stream.status !== "live") state.setStream({ status: "unreachable" });
    }, 5_000);
  }
}

export function close(): void {
  source?.close();
  source = null;
  if (watchdog !== null) {
    window.clearInterval(watchdog);
    watchdog = null;
  }
}

/** A laptop closed and reopened: if the stream isn't live, open it again. The resume point is the
 *  lowest head the tab holds, so nothing in between is missed, or the server answers `resync`. */
export function onWake(): void {
  if (useCompetition.getState().stream.status !== "live") open();
}
